import { Button, Icons, useToggle } from '@commenda/kakashi';
import {
  LegacyDocumentType,
  TaskParty,
  TaskStatus,
} from '@prisma/client';
import { ServiceRequestTaskLabel } from '../../../common/labels';
import { CorporationUploadFileModal } from '../../../common/modals/CorporationUploadFileModal';
import { ServiceRequestTaskIconConfig } from '../../../common/taskIconConfig';
import { useServiceRequestFile } from '../../../hook/useServiceRequestFile';
import { ServiceRequestTaskCard } from '../../../layout/components/taskCard/ServiceRequestTaskCard';
import { ServiceRequestTaskCardConfig, TCommonTaskProp } from '../../../types';
import { updateTaskStatus, viewUploadedFile } from '../../../utils';

export const UploadApprovedLLPDeedTask = ({
  task,
  context,
  isTimelineView,
  displayNumber,
}: TCommonTaskProp) => {
  const { role, serviceRequest, mutate } = context;

  const uploadModalVisibility = useToggle();

  const [file, setFile] = useServiceRequestFile(
    serviceRequest,
    LegacyDocumentType.LLPDeed
  );

  const handleViewFile = () => viewUploadedFile(file.id);

  const config: ServiceRequestTaskCardConfig = {
    getLeftIcon: (status) => ServiceRequestTaskIconConfig[status],
    getStateConfig: (state) => {
      const stateMap = {
        [TaskParty.AGENT]: {
          [TaskStatus.TO_DO]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel label="Client uploads signed LLP Deed (including stamp paper)" />
            ),
          },
          [TaskStatus.COMPLETED]: {
            actions: file?.id ? (
              <Button size="sm" variant="secondary" onClick={handleViewFile}>
                View upload
              </Button>
            ) : null,
            label: (
              <ServiceRequestTaskLabel
                label="LLP deed (including stamp paper) submitted by client"
                timestamp={task.updatedAt}
                status={task.status}
              />
            ),
          },
        },
        [TaskParty.CLIENT]: {
          [TaskStatus.TO_DO]: {
            actions: (
              <Button
                size="sm"
                onClick={uploadModalVisibility.show}
                variant="primary"
              >
                <Icons.Upload01 className="size-4" />
                Upload
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel label="Sign & upload LLP Deed (including stamp paper)" />
            ),
          },
          [TaskStatus.BACKLOG]: {
            actions: (
              <Button size="sm" disabled variant="primary">
                <Icons.Upload01 className="size-4" />
                Upload
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel label="Sign & upload LLP Deed (including stamp paper)" />
            ),
          },
          [TaskStatus.COMPLETED]: {
            actions: file?.id ? (
              <Button size="sm" variant="secondary" onClick={handleViewFile}>
                View upload
              </Button>
            ) : null,
            label: (
              <ServiceRequestTaskLabel
                label="LLP Deed Uploaded"
                timestamp={task.updatedAt}
                status={task.status}
              />
            ),
          },
        },
      };

      return (
        stateMap[state.role]?.[state.status] ?? {
          label: task.title,
          actions: null,
        }
      );
    },
  };

  return (
    <div>
      <ServiceRequestTaskCard
        task={task}
        state={{
          role,
          status: task.status,
        }}
        config={config}
        context={context}
        isTimelineView={isTimelineView}
        displayNumber={displayNumber}
      />

      <CorporationUploadFileModal
        serviceRequest={serviceRequest}
        serviceRequestTaskId={task.id}
        isOpen={uploadModalVisibility.visible}
        setIsOpen={uploadModalVisibility.hide}
        file={file}
        setFile={setFile}
        heading="Upload signed LLP Deed"
        documentType={LegacyDocumentType.LLPDeed}
        onUploadSuccess={async () => {
          await updateTaskStatus({
            task,
            status: TaskStatus.COMPLETED,
            mutate,
          });
        }}
      />
    </div>
  );
};
